import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DeleteOutlined } from "@mui/icons-material";
import VirtualizedAutocomplete from "../../../componentes/virtualizedAutocomplete";
import { Variaveis } from "../../../pages/easyReport/newFunction/Index";
import { SELECT_ALL_KEY, SELECT_ALL_OPTION } from "../constants/const";

interface Application {
  name: string;
}

interface VariableFormProps {
  variable: Variaveis;
  index: number;
  allVariables: Variaveis[];
  allApplications: Application[];
  itemsOptions: { name: string; key_: string; applicationName: string }[];
  itemsLoading: boolean;
  canDelete: boolean;
  styles: any;
  onVariableChange: (
    index: number,
    updatedVariable: Partial<Variaveis>,
  ) => void;
  onApplicationChange: (
    index: number,
    variable: Variaveis,
    newValues: Application[],
  ) => void;
  onDelete: (index: number) => void;
  isFieldDuplicated: (
    variables: Variaveis[],
    value: string,
    field: keyof Pick<Variaveis, "variableName" | "title">,
  ) => boolean;
}

export function VariableForm({
  variable,
  index,
  allVariables,
  allApplications,
  itemsOptions,
  itemsLoading,
  canDelete,
  styles,
  onVariableChange,
  onApplicationChange,
  onDelete,
  isFieldDuplicated,
}: VariableFormProps) {
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.down("lg"));
  const isCustom = itemsOptions[0]?.applicationName === "CUSTOM";

  const selectedItems = itemsOptions.filter((item) =>
    variable.keyName.some(
      (v) => v.key === item.key_ && v.applicationName === item.applicationName,
    ),
  );
  const showSelectAll = !isCustom && itemsOptions.length > 0;
  const allSelected =
    showSelectAll && selectedItems.length === itemsOptions.length;
  const itemOptionsWithSelectAll = showSelectAll
    ? [SELECT_ALL_OPTION, ...itemsOptions]
    : itemsOptions;

  function handleKeyNameChange(
    newValues: unknown,
    index: number,
    onVariableChange: (
      index: number,
      updatedVariable: Partial<Variaveis>,
    ) => void,
  ) {
    const keyNameArray = Array.isArray(newValues)
      ? newValues
          .filter(
            (
              nv,
            ): nv is {
              name: string;
              key_: string;
              applicationName: string;
            } =>
              typeof nv === "object" &&
              nv !== null &&
              "name" in nv &&
              "key_" in nv &&
              "applicationName" in nv,
          )
          .map((nv) => ({
            key: nv.key_,
            applicationName: nv.applicationName,
          }))
      : [];
    onVariableChange(index, { keyName: keyNameArray });
  }

  return (
    <>
      <Box sx={styles.variableContainer}>
        {/**
         *    <Box sx={{ flex: 1 }}>
                     <Box sx={styles.boxTitle}>
                       <Article color="primary" />
                       <Typography variant="subtitle2">Descrição</Typography>
                     </Box>
         
                     <TextField
                       fullWidth
                       size="small"
                       variant="outlined"
                       slotProps={{
                         input: {
                           sx: {
                             color: theme.palette.text.secondary,
                           },
                         },
                       }}
                       value={newFuncao.description}
                       onChange={(e) =>
                         setNewFuncao((prev) => ({
                           ...prev,
                           description: e.target.value,
                         }))
                       }
                       placeholder="Descrição"
                     />
                   </Box>
         */}
        <Box sx={{ flex: 1 }}>
          <Box sx={styles.boxTitle}>
            <Typography variant="subtitle2">Label</Typography>
          </Box>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Label da variável"
            slotProps={{
              input: {
                sx: {
                  color: theme.palette.text.secondary,
                },
              },
            }}
            value={variable.title}
            onChange={(e) =>
              onVariableChange(index, { title: e.target.value || "" })
            }
            error={isFieldDuplicated(allVariables, variable.title, "title")}
            helperText={
              isFieldDuplicated(allVariables, variable.title, "title")
                ? "Label da variável duplicado"
                : "O nome que será exibido"
            }
          />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Box sx={styles.boxTitle}>
            <Typography variant="subtitle2">Variável</Typography>
          </Box>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Nome da variável"
            slotProps={{
              input: {
                sx: {
                  color: theme.palette.text.secondary,
                },
              },
            }}
            value={variable.variableName}
            onChange={(e) =>
              onVariableChange(index, { variableName: e.target.value || "" })
            }
            error={isFieldDuplicated(
              allVariables,
              variable.variableName,
              "variableName",
            )}
            helperText={
              isFieldDuplicated(
                allVariables,
                variable.variableName,
                "variableName",
              )
                ? "Nome da variável duplicado"
                : "O nome que será usado no código"
            }
          />
        </Box>

        <Box sx={{ flex: -2, mb: 3 }}>
          <Box sx={styles.boxTitle}>
            <Typography variant="subtitle2">Aplicação</Typography>
          </Box>
          <Autocomplete
            fullWidth
            multiple
            size="small"
            limitTags={1}
            disableCloseOnSelect
            // sx={styles.autocomplete}
            options={allApplications}
            getOptionLabel={(option) => option.name || ""}
            value={allApplications.filter((app) =>
              variable.applicationId.includes(app.name),
            )}
            onChange={(_, newValue) =>
              onApplicationChange(index, variable, newValue)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Selecione a aplicação"
                variant="outlined"
              />
            )}
            renderOption={(props, option, { selected }) => {
              const { key, ...rest } = props;
              return (
                <li key={key} {...rest}>
                  <Checkbox checked={selected} />
                  {option.name}
                </li>
              );
            }}
          />
        </Box>

        <Box sx={{ flex: 1, mb: 3 }}>
          <Box sx={styles.boxTitle}>
            <Typography variant="subtitle2">Itens</Typography>
          </Box>
          <VirtualizedAutocomplete
            fullWidth
            multiple
            size="small"
            limitTags={1}
            disableCloseOnSelect
            //sx={styles.autocomplete}
            loading={itemsLoading}
            options={itemOptionsWithSelectAll}
            value={selectedItems}
            filterOptions={(options, { inputValue }) =>
              options.filter(
                (option) =>
                  option.name
                    .toLowerCase()
                    .includes(inputValue.toLowerCase()) ||
                  option.key_.toLowerCase().includes(inputValue.toLowerCase()),
              )
            }
            onChange={(_, newValues) => {
              const containsSelectAll =
                Array.isArray(newValues) &&
                newValues.some((nv: any) => nv?.key_ === SELECT_ALL_KEY);
              if (containsSelectAll) {
                handleKeyNameChange(
                  allSelected ? [] : itemsOptions,
                  index,
                  onVariableChange,
                );
              } else if (isCustom) {
                const arr = Array.isArray(newValues) ? newValues : [];
                handleKeyNameChange(
                  arr.length > 0 ? [arr[arr.length - 1]] : [],
                  index,
                  onVariableChange,
                );
              } else {
                handleKeyNameChange(newValues, index, onVariableChange);
              }
            }}
            renderOption={(props, option, { selected }) => {
              const { key, ...rest } = props;
              if ((option as any)?.key_ === SELECT_ALL_KEY) {
                return (
                  <li key={key} {...rest}>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={selectedItems.length > 0 && !allSelected}
                    />
                    <Typography sx={{ fontWeight: 600 }}>
                      {option.name}
                    </Typography>
                  </li>
                );
              }
              return (
                <li key={key} {...rest}>
                  <Checkbox checked={selected} />
                  <Tooltip
                    title={
                      <Box>
                        <Typography sx={{ fontSize: 11 }}>
                          {option.name}
                        </Typography>
                        <Typography
                          sx={{ fontSize: 11 }}
                          color="text.secondary"
                        >
                          {option.key_}
                        </Typography>
                      </Box>
                    }
                  >
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography
                        sx={{
                          maxWidth: 250,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {option.name}
                      </Typography>
                      <Typography
                        sx={{
                          maxWidth: 250,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          color: theme.palette.text.disabled,
                        }}
                      >
                        {option.key_}
                      </Typography>
                    </Box>
                  </Tooltip>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Selecione os itens"
                variant="outlined"
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {itemsLoading ? (
                          <CircularProgress size={22} sx={{ mr: 1 }} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
          }}
        >
          {variable.applicationId.length >= 0 &&
          !variable.applicationId.includes("CUSTOM") ? (
            <>
              <Box sx={styles.boxTitle}>
                <Typography variant="subtitle2">Múltiplos</Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                  mb: 3,
                }}
              >
                <FormControl
                  sx={{ ...styles.multiBox, flexShrink: 0 }}
                  size="small"
                >
                  {/*     <FormLabel sx={styles.boxTitle} id="row-radio-buttons-group-label">
              Multiplos
            </FormLabel> */}
                  <RadioGroup
                    row
                    aria-label="Múltiplos"
                    name={`radio-multiplos-${index}`}
                    value={variable.multi ? "true" : "false"}
                    onChange={(_, newValue) =>
                      onVariableChange(index, { multi: newValue === "true" })
                    }
                  >
                    <FormControlLabel
                      value="false"
                      control={<Radio size="small" />}
                      sx={{ transform: "scale(0.9)" }}
                      label="Não"
                    />
                    <FormControlLabel
                      value="true"
                      control={<Radio size="small" />}
                      sx={{ transform: "scale(0.9)" }}
                      label="Sim"
                    />
                  </RadioGroup>
                </FormControl>
                {!isLarge && (
                  <IconButton
                    aria-label="Remover variável"
                    onClick={() => onDelete(index)}
                    disabled={!canDelete}
                    sx={{ ...styles.iconButton, flex: 1, minWidth: 0 }}
                  >
                    <DeleteOutlined sx={{ fontSize: 30 }} />
                  </IconButton>
                )}
              </Box>
            </>
          ) : (
            !isLarge && (
              <IconButton
                aria-label="Remover variável"
                onClick={() => onDelete(index)}
                disabled={!canDelete}
                sx={{ ...styles.iconButton, flex: 1, minWidth: 0 }}
              >
                <DeleteOutlined sx={{ fontSize: 30 }} />
              </IconButton>
            )
          )}
        </Box>
      </Box>

      {isLarge && (
        <Button
          variant="outlined"
          color="error"
          disabled={!canDelete}
          sx={{ flex: 1, minWidth: 0 }}
          onClick={() => onDelete(index)}
        >
          <DeleteOutlined sx={{ fontSize: 30 }} /> Deletar Variável
        </Button>
      )}

      {isLarge && <Divider sx={{ my: 1 }} />}
    </>
  );
}
